import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Modal from '@components/Modals';
import { useAuthContext } from '@hooks/use-auth-context';
import {
  Section,
  SettingsSectionBox,
  SettingsButton,
  SettingsSectionLabel,
} from '@views/Settings/components';
import { XIcon } from '@components/Icons';
import clayAppSumoLogo from '@app/img/login/clay-app-sumo.png';
import { PersonalPlanBox, ProPlanBox, TeamsPlanBox } from './PlanBoxes';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import { BillingProvider, ClaySubscription } from '@app/types';
import styles from './ManagePlan.module.scss';
import openURL from '@support/open-url';
import { AuthContextSchema } from '@app/Context/AuthContext';
import { selectUserContactCount } from '@reducers/user/stats';
import upgradeAction from '@support/upgrade-action';
import { supportHref } from '@support/preferred-email-client';

export function ManageAppSumoPlanModal({
  closeModal,
  subscription,
}: {
  closeModal: () => void;
  subscription: ClaySubscription;
}) {
  return (
    <Modal isOpen={true} className="modal-small" onRequestClose={closeModal}>
      <div className={cn(styles.modal, 'modal-content-padded')}>
        <img className={styles.logo} src={clayAppSumoLogo} alt="AppSumo + Clay" />
        <p>Your subscription was purchased through AppSumo. This gives you:</p>
        <ul>
          <li>Up to 5,000 People in Clay</li>
          <li>Access to all Clay Pro features</li>
          <li>A lifetime subscription to Clay</li>
        </ul>
        <p>To manage your Clay subscription with AppSumo, please click below.</p>
        <div className={styles.options}>
          <button type="button" className="btn btn-muted" onClick={closeModal}>
            Cancel
          </button>
          <a className="btn btn-primary" href={subscription.management_url} target="_blank">
            Manage Subscription
          </a>
        </div>
        <XIcon className={styles.close} onClick={closeModal} hoverOpacity />
      </div>
    </Modal>
  );
}

const ContactUsage = () => {
  const { contactLimit } = useAuthContext();
  const userContactCount = useSelector(selectUserContactCount);
  return (
    <div className={styles.contactUsage}>
      {(userContactCount || 0).toLocaleString()} of {contactLimit.toLocaleString()} contacts used
    </div>
  );
};

const billingProviderButtonLabels: Record<BillingProvider, string> = {
  AppSumo: 'AppSumo Plan Details',
  Stripe: 'Manage Plan',
  RevenueCat: 'Manage in App Store',
};

const ManagePlanButtons = () => {
  const dispatch = useDispatch();
  const { subscriptions, user } = useAuthContext();
  const [appSumoModalOpen, setAppSumoModalOpen] = useState(false);
  const appSumoSubscription = subscriptions.find((s) => s.plan.provider === 'AppSumo');
  const otherSubscriptions = subscriptions.filter((s) => s.plan.provider !== 'AppSumo');
  const closeAppSumoModal = () => setAppSumoModalOpen(false);

  return (
    <div className={styles.managePlanBtns}>
      {otherSubscriptions.length > 0 ? (
        otherSubscriptions.map((sub) => (
          <SettingsButton
            key={sub.id}
            onClick={() => openURL(sub.management_url)}
            className={styles.btn}
            isInsideBox
          >
            {billingProviderButtonLabels[sub.plan.provider]}
          </SettingsButton>
        ))
      ) : (
        <SettingsButton onClick={upgradeAction(user, dispatch)} className={styles.btn} isInsideBox>
          Get Unlimited Contacts
        </SettingsButton>
      )}
      {appSumoSubscription && (
        <SettingsButton
          onClick={() => setAppSumoModalOpen(true)}
          className={styles.btn}
          isInsideBox
        >
          {billingProviderButtonLabels.AppSumo}
        </SettingsButton>
      )}
      {appSumoModalOpen && (
        <ManageAppSumoPlanModal closeModal={closeAppSumoModal} subscription={appSumoSubscription} />
      )}
    </div>
  );
};

export const getPlanType = (authCtx: AuthContextSchema) => {
  let planType = 'free';
  if (authCtx.getIsProPlan()) {
    planType = 'pro';
  } else if (authCtx.getIsTeamsPlan()) {
    planType = 'teams';
  }
  return planType;
};

export default function ManagePlan() {
  const authCtx = useAuthContext();
  const workspaceCtx = useWorkspaceContext();
  const user = useRef(authCtx.user);
  const planType = useRef(getPlanType(authCtx));
  const contactLimit = useRef(authCtx.contactLimit);
  const showContactAdminMsg = useRef(workspaceCtx.activeWorkspace && !workspaceCtx.userIsAdmin);

  let managePlan = (
    <div className={styles.btns}>
      <ManagePlanButtons />
    </div>
  );

  if (showContactAdminMsg.current) {
    managePlan = (
      <div className={styles.contactAdmin}>
        Contact your admin to
        <br />
        manage this plan
      </div>
    );
  } else if (window.desktopIntegrations?.appInfo?.isMAS) {
    managePlan = (
      <SettingsButton
        href={supportHref({ subject: `Manage ${user.current.primary_product.name} Plan` })}
        className={styles.btn}
        isInsideBox
        asLink
      >
        Contact Support
      </SettingsButton>
    );
  }

  useEffect(() => {
    if (authCtx.user) {
      user.current = authCtx.user;
      planType.current = getPlanType(authCtx);
      contactLimit.current = authCtx.contactLimit;
    }
  }, [authCtx.user]);

  useEffect(() => {
    if (workspaceCtx.activeWorkspace) {
      showContactAdminMsg.current = workspaceCtx.activeWorkspace && !workspaceCtx.userIsAdmin;
    }
  }, [workspaceCtx.activeWorkspace]);

  return (
    <>
      <Section>
        <SettingsSectionLabel>Subscription Plan</SettingsSectionLabel>
        <SettingsSectionBox
          className={cn(styles.box, styles[user.current.primary_product.name.split(' ').join('-')])}
        >
          {planType.current === 'free' && <PersonalPlanBox />}
          {planType.current === 'pro' && <ProPlanBox />}
          {planType.current === 'teams' && <TeamsPlanBox />}
          <div className={styles.planDetails}>
            {user.current.primary_product.name}
            {!!contactLimit.current && <ContactUsage />}
          </div>
          {managePlan}
        </SettingsSectionBox>
      </Section>
    </>
  );
}
